import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { WantToWatchEntry } from '../types'

interface UseWantToWatchResult {
  entries: WantToWatchEntry[]
  loading: boolean
  error: string | null
  add: (productionId: string) => Promise<void>
  remove: (productionId: string) => Promise<void>
  isOnList: (productionId: string) => boolean
}

export function useWantToWatch(userId: string | undefined): UseWantToWatchResult {
  const [entries, setEntries] = useState<WantToWatchEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!userId) {
      setLoading(false)
      return
    }

    async function fetchEntries() {
      setLoading(true)
      setError(null)

      const { data, error: supabaseError } = await supabase
        .from('want_to_watch')
        .select('*, productions(id, title, type, year, company_or_studio, poster_image_url, plays(id, title))')
        .eq('user_id', userId)
        .order('added_at', { ascending: false })

      if (supabaseError) {
        setError(supabaseError.message)
      } else {
        setEntries((data ?? []) as WantToWatchEntry[])
      }

      setLoading(false)
    }

    fetchEntries()
  }, [userId])

  const add = useCallback(async (productionId: string) => {
    if (!userId) return

    const { data, error: supabaseError } = await supabase
      .from('want_to_watch')
      .insert({ user_id: userId, production_id: productionId })
      .select('*, productions(id, title, type, year, company_or_studio, poster_image_url, plays(id, title))')
      .single()

    if (supabaseError) {
      setError(supabaseError.message)
    } else {
      setEntries(prev => [data as WantToWatchEntry, ...prev])
    }
  }, [userId])

  const remove = useCallback(async (productionId: string) => {
    if (!userId) return

    const { error: supabaseError } = await supabase
      .from('want_to_watch')
      .delete()
      .eq('user_id', userId)
      .eq('production_id', productionId)

    if (supabaseError) {
      setError(supabaseError.message)
    } else {
      setEntries(prev => prev.filter(e => e.production_id !== productionId))
    }
  }, [userId])

  const isOnList = useCallback(
    (productionId: string) => entries.some(e => e.production_id === productionId),
    [entries]
  )

  return { entries, loading, error, add, remove, isOnList }
}
